import { useRef } from 'react'
import type { Overlay } from '@shared/types'
import { useEditorStore } from '../../../store/editorStore'
import { collectSnapTargets, computeOverlayLeftTrim, getSegmentTimeline, secToPx, snapSec } from '../../../lib/timelineMath'
import { SNAP_TOLERANCE_PX } from './constants'

const KIND_COLOR: Record<Overlay['kind'], string> = {
  title: '#E6B422',
  label: '#35C7E0',
  end: '#FF4D5E',
  logo: '#8FA0B8',
  free: '#B48CFF',
}

const KIND_LABEL: Record<Overlay['kind'], string> = {
  title: 'タイトル',
  label: 'ラベル',
  end: '終了カード',
  logo: 'ロゴ',
  free: 'フリー',
}

/** task spec §2-9: Overlay 端トリムの最小 durationSec。 */
const MIN_DURATION_SEC = 0.2

type DragMode = 'move' | 'trim-right' | 'trim-left'

export function OverlayBar({
  overlay,
  selected,
  pxPerSec,
}: {
  overlay: Overlay
  selected: boolean
  pxPerSec: number
}) {
  const updateOverlay = useEditorStore((s) => s.updateOverlay)
  const select = useEditorStore((s) => s.select)
  const toggleSelection = useEditorStore((s) => s.toggleSelection)
  const beginHistoryBatch = useEditorStore((s) => s.beginHistoryBatch)
  const endHistoryBatch = useEditorStore((s) => s.endHistoryBatch)
  const snapEnabled = useEditorStore((s) => s.snapEnabled)
  const setSnapGuideSec = useEditorStore((s) => s.setSnapGuideSec)
  const drag = useRef<{ mode: DragMode; x: number; startSec: number; durationSec: number } | null>(null)

  if (overlay.kind === 'logo') return null // persistent watermark, not a timed block (own lane row instead)

  const left = secToPx(overlay.startSec, pxPerSec)
  const width = Math.max(10, overlay.durationSec * pxPerSec)

  /**
   * UI_MK2_PHASE2.md §2-5: 左端トリムは `collectSnapTargets`（第1段の一般版）を
   * 使う。`excludeValue` はドラッグ開始時点の自分の座標（trim-left なら開始
   * 前の startSec）— これ自身と一致する候補（例えば隣接する別 Overlay の端が
   * たまたま同じ秒に置かれている場合）に吸着してドラッグの初手が動かなく
   * なる事故を防ぐ（SegmentBlock の `excludeCoincidentSec` と同じ狙い）。
   */
  function resolveSnap(rawSec: number, excludeValue?: number): number {
    if (!snapEnabled) return rawSec
    const store = useEditorStore.getState()
    const comp = store.composition
    if (!comp) return rawSec
    const timeline = getSegmentTimeline(comp.tracks.video)
    let targets = collectSnapTargets(timeline, comp.tracks.overlays, store.playback.currentSec, {
      excludeOverlayId: overlay.id,
    })
    if (excludeValue != null) {
      const EPS = 1e-6
      targets = targets.filter((v) => Math.abs(v - excludeValue) > EPS)
    }
    const snapped = snapSec(rawSec, targets, SNAP_TOLERANCE_PX / pxPerSec)
    setSnapGuideSec(snapped.snappedTo)
    return snapped.sec
  }

  function endDrag(e: React.PointerEvent) {
    if (drag.current) {
      ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
      endHistoryBatch()
      setSnapGuideSec(null)
    }
    drag.current = null
  }

  return (
    <div
      onPointerDown={(e) => {
        drag.current = { mode: 'move', x: e.clientX, startSec: overlay.startSec, durationSec: overlay.durationSec }
        beginHistoryBatch()
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
      }}
      onPointerMove={(e) => {
        if (!drag.current || drag.current.mode !== 'move') return
        const deltaSec = (e.clientX - drag.current.x) / pxPerSec
        const raw = Math.max(0, drag.current.startSec + deltaSec)
        const next = Number(resolveSnap(raw).toFixed(2))
        // reviewer 差し戻し m2: 値が変わらないなら mutate を呼ばない。
        if (next !== overlay.startSec) updateOverlay(overlay.id, { startSec: next })
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(e) => {
        e.stopPropagation()
        if (e.shiftKey) toggleSelection({ kind: 'overlay', id: overlay.id })
        else select({ kind: 'overlay', id: overlay.id })
      }}
      data-testid="overlay-bar"
      data-overlay-kind={overlay.kind}
      className={`absolute top-1 bottom-1 rounded px-1.5 flex items-center text-[9px] font-medium text-black/80 cursor-grab active:cursor-grabbing border ${
        selected ? 'border-white' : 'border-transparent'
      }`}
      style={{ left, width, backgroundColor: KIND_COLOR[overlay.kind] }}
      title={`${KIND_LABEL[overlay.kind]} ${overlay.startSec.toFixed(1)}s 〜 ${(overlay.startSec + overlay.durationSec).toFixed(1)}s`}
    >
      <span className="truncate pointer-events-none">{KIND_LABEL[overlay.kind]}</span>
      {/* UI_MK2_PHASE2.md §2-5: 左端ドラッグで startSec/durationSec を同時に
          調整（end = startSec+durationSec を固定）。第1段では右トリムのみ
          実装済みだったための見送り分。 */}
      <div
        onPointerDown={(e) => {
          e.stopPropagation()
          drag.current = { mode: 'trim-left', x: e.clientX, startSec: overlay.startSec, durationSec: overlay.durationSec }
          beginHistoryBatch()
          ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          if (!drag.current || drag.current.mode !== 'trim-left') return
          e.stopPropagation()
          const deltaSec = (e.clientX - drag.current.x) / pxPerSec
          const fixedEndSec = drag.current.startSec + drag.current.durationSec
          const rawStart = drag.current.startSec + deltaSec
          const snappedStart = resolveSnap(rawStart, drag.current.startSec)
          const { startSec: clampedStart, durationSec: rawDuration } = computeOverlayLeftTrim(
            fixedEndSec,
            snappedStart,
            MIN_DURATION_SEC,
          )
          const nextStart = Number(clampedStart.toFixed(2))
          const nextDuration = Number(rawDuration.toFixed(2))
          // reviewer 差し戻し m2 と同じ流儀: 値が変わらないなら mutate を呼ばない。
          if (nextStart !== overlay.startSec || nextDuration !== overlay.durationSec) {
            updateOverlay(overlay.id, { startSec: nextStart, durationSec: nextDuration })
          }
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-white/40"
        data-testid="overlay-trim-left"
      />
      <div
        onPointerDown={(e) => {
          e.stopPropagation()
          drag.current = { mode: 'trim-right', x: e.clientX, startSec: overlay.startSec, durationSec: overlay.durationSec }
          beginHistoryBatch()
          ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          if (!drag.current || drag.current.mode !== 'trim-right') return
          e.stopPropagation()
          const deltaSec = (e.clientX - drag.current.x) / pxPerSec
          const rawEnd = drag.current.startSec + drag.current.durationSec + deltaSec
          const snappedEnd = resolveSnap(rawEnd)
          const nextDuration = Number(Math.max(MIN_DURATION_SEC, snappedEnd - drag.current.startSec).toFixed(2))
          // reviewer 差し戻し m2: 値が変わらないなら mutate を呼ばない。
          if (nextDuration !== overlay.durationSec) updateOverlay(overlay.id, { durationSec: nextDuration })
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-white/40"
        data-testid="overlay-trim-right"
      />
    </div>
  )
}
